import { useMemo } from 'react'
import type { SeriesPoint } from '../../hooks/useAnalytics'

/**
 * Stacked bars over time, optionally split by a grouping dimension.
 *
 * Deliberately the same plain-CSS idiom as the existing TimelineChart rather
 * than a charting dependency: the console has no chart library and one bar per
 * bucket is all these series need.
 */

export interface SeriesChartProps {
  series: SeriesPoint[]
  /** Which measure to plot. */
  metric?: 'interactions' | 'events' | 'navViews' | 'apiCalls' | 'uniqueUsers' | 'uniqueVisitors'
  /** Render a legend + stacked segments when the series is grouped. */
  grouped?: boolean
  labelFor?: (key: string) => string
  height?: number
}

/**
 * Stable, deterministic colour per group key, so a version keeps its colour
 * across renders and between charts.
 */
const PALETTE = [
  'bg-seed-green-500', 'bg-sky-500', 'bg-violet-500', 'bg-amber-500',
  'bg-rose-500', 'bg-teal-500', 'bg-indigo-500', 'bg-orange-500',
]

function colorFor(key: string, order: string[]): string {
  const index = order.indexOf(key)
  return PALETTE[(index < 0 ? 0 : index) % PALETTE.length]
}

export function SeriesChart({
  series,
  metric = 'interactions',
  grouped = false,
  labelFor = k => k,
  height = 180,
}: SeriesChartProps) {
  const { periods, groups, byPeriod, max } = useMemo(() => {
    const periodSet = new Map<string, Map<string, number>>()
    const totals = new Map<string, number>()

    for (const point of series) {
      const key = grouped ? (point.groupKey ?? '—') : 'total'
      if (!periodSet.has(point.period)) periodSet.set(point.period, new Map())
      const bucket = periodSet.get(point.period)!
      const value = Number(point[metric] ?? 0)
      bucket.set(key, (bucket.get(key) ?? 0) + value)
      totals.set(key, (totals.get(key) ?? 0) + value)
    }

    const periods = [...periodSet.keys()].sort()
    // Biggest groups first, so the legend reads in order of importance and
    // colours stay stable while the range changes.
    const groups = [...totals.entries()].sort((a, b) => b[1] - a[1]).map(([k]) => k)
    const max = Math.max(
      ...periods.map(p => [...periodSet.get(p)!.values()].reduce((a, b) => a + b, 0)),
      1,
    )
    return { periods, groups, byPeriod: periodSet, max }
  }, [series, metric, grouped])

  if (series.length === 0) {
    return <div className="py-12 text-center text-sm text-muted-foreground">No activity in this range</div>
  }

  return (
    <div>
      <div className="flex items-end gap-px" style={{ height }}>
        {periods.map(period => {
          const bucket = byPeriod.get(period)!
          const total = [...bucket.values()].reduce((a, b) => a + b, 0)
          return (
            <div key={period} className="group relative flex-1 flex flex-col justify-end" style={{ minWidth: 3 }}>
              {groups
                .filter(g => (bucket.get(g) ?? 0) > 0)
                .map(g => (
                  <div
                    key={g}
                    className={`w-full ${colorFor(g, groups)} first:rounded-t transition-opacity group-hover:opacity-80`}
                    style={{ height: Math.max(((bucket.get(g) ?? 0) / max) * (height - 20), 1) }}
                  />
                ))}
              <div className="pointer-events-none absolute bottom-full left-1/2 z-10 mb-1 hidden -translate-x-1/2 whitespace-nowrap rounded border bg-popover px-2 py-1 text-xs text-popover-foreground shadow-md group-hover:block">
                <div className="font-medium">{period}</div>
                <div>{total.toLocaleString()} {metric === 'events' ? 'events' : metric}</div>
                {grouped && groups.filter(g => (bucket.get(g) ?? 0) > 0).slice(0, 6).map(g => (
                  <div key={g} className="flex items-center gap-1.5 text-muted-foreground">
                    <span className={`inline-block size-2 rounded-full ${colorFor(g, groups)}`} />
                    {labelFor(g)}: {(bucket.get(g) ?? 0).toLocaleString()}
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      </div>

      <div className="mt-1 flex justify-between text-xs text-muted-foreground">
        <span>{periods[0]}</span>
        <span>{periods[periods.length - 1]}</span>
      </div>

      {grouped && groups.length > 1 && (
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
          {groups.slice(0, 12).map(g => (
            <span key={g} className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <span className={`inline-block size-2 rounded-full ${colorFor(g, groups)}`} />
              {labelFor(g)}
            </span>
          ))}
        </div>
      )}
    </div>
  )
}
